import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Settings,
  Menu,
  X,
  BarChart3,
  LogOut,
  ChevronRight,
  Sparkles,
  Compass,
} from "lucide-react";
import { UserProfile, AuthUser } from "../types";

interface NavigationProps {
  userProfile: UserProfile | null;
  authUser: AuthUser | null;
  onSignOut: () => void;
  onOpenAuthModal: (mode: "signin" | "signup") => void;
  onActionRequiringInfo?: (action: string) => void;
  onTakeTour?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ 
  userProfile, 
  authUser, 
  onSignOut, 
  onOpenAuthModal,
  onTakeTour,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Core navigation items - simplified to 2 primary tabs
  const coreNavItems = [
    { path: "/my-golf-bag", label: "My Bag", icon: BarChart3, color: "emerald" },
    { path: "/club-optimizer", label: "Optimizer", icon: Settings, color: "purple" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-gray-900/5 border-b border-gray-200/50' 
            : 'bg-white border-b border-gray-200'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-[max(0px,env(safe-area-inset-top,0px))]">
          <div className="flex items-center justify-between h-16">

            {/* Logo */}
            <Link to="/home" className="flex items-center gap-3 group">
              <div className="relative">
                <img 
                  src="/Images/New Logo Image.png" 
                  alt="ClubIQ Logo" 
                  className="h-10 w-auto rounded-xl shadow-sm group-hover:shadow-md transition-shadow"
                />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                ClubIQ
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {coreNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                      transition-all duration-200 group
                      ${active
                        ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon 
                      size={18} 
                      className={`transition-transform group-hover:scale-110 ${
                        active ? 'text-indigo-600' : ''
                      }`} 
                    />
                    <span>{item.label}</span>
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Tablet Navigation - Icons Only */}
            <div className="hidden md:flex lg:hidden items-center gap-1">
              {coreNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`
                      relative p-2.5 rounded-xl transition-all duration-200 group
                      ${active
                        ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                      }
                    `}
                    title={item.label}
                  >
                    <Icon size={20} className="transition-transform group-hover:scale-110" />
                    {active && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right Side - Auth */}
            <div className="hidden md:flex items-center gap-3">
              {authUser ? (
                <Link
                  to="/profile"
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                    transition-all duration-200 group
                    ${isActive("/profile")
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  <div className={`
                    w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                    ${isActive("/profile")
                      ? 'bg-white/20'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                    }
                  `}>
                    {authUser.firstName?.charAt(0)}{authUser.lastName?.charAt(0)}
                  </div>
                  <span className="hidden xl:inline">{authUser.firstName}</span>
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => onOpenAuthModal("signin")}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => onOpenAuthModal("signup")}
                    className="
                      flex items-center gap-2 px-5 py-2.5 
                      bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                      rounded-xl text-sm font-semibold
                      shadow-lg shadow-indigo-500/25
                      hover:shadow-xl hover:shadow-indigo-500/30
                      hover:-translate-y-0.5
                      transition-all duration-200
                    "
                  >
                    <Sparkles size={16} />
                    Get Started
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`
          fixed inset-0 z-40 md:hidden
          transition-opacity duration-300
          ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`
            absolute top-16 left-0 right-0 
            bg-white border-b border-gray-200 shadow-xl
            transition-all duration-300 ease-out
            ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
          `}
        >
          <div className="p-4 space-y-2">
            {coreNavItems.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    flex items-center justify-between p-4 rounded-2xl
                    transition-all duration-200
                    ${active
                      ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }
                  `}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className={`
                      w-11 h-11 rounded-xl flex items-center justify-center
                      ${active
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                        : 'bg-white text-gray-500 shadow-sm'
                      }
                    `}>
                      <Icon size={20} />
                    </div>
                    <span className={`font-semibold ${active ? 'text-indigo-900' : 'text-gray-900'}`}>
                      {item.label}
                    </span>
                  </div>
                  <ChevronRight size={18} className={active ? 'text-indigo-500' : 'text-gray-400'} />
                </Link>
              );
            })}
          </div>

          {/* Auth Section */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {authUser ? (
              <div className="space-y-2">
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-500/30">
                    {authUser.firstName?.charAt(0)}{authUser.lastName?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{authUser.firstName} {authUser.lastName}</p>
                    <p className="text-sm text-gray-500">View Profile</p>
                  </div>
                  <ChevronRight size={18} className="text-gray-400" />
                </Link>
                {onTakeTour && (
                  <button
                    onClick={() => {
                      onTakeTour();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 w-full p-4 text-emerald-600 hover:bg-emerald-50 rounded-2xl transition-colors"
                  >
                    <Compass size={18} />
                    <span className="font-medium">Take the Tour</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    onSignOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 w-full p-4 text-rose-600 hover:bg-rose-50 rounded-2xl transition-colors"
                >
                  <LogOut size={18} />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    onOpenAuthModal("signin");
                    setIsMobileMenuOpen(false);
                  }}
                  className="p-4 bg-white border-2 border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    onOpenAuthModal("signup");
                    setIsMobileMenuOpen(false);
                  }}
                  className="
                    p-4 bg-gradient-to-r from-indigo-600 to-purple-600 
                    rounded-2xl text-white font-semibold
                    shadow-lg shadow-indigo-500/30
                    hover:shadow-xl transition-all
                  "
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
